import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import {
  matchPastedRoomTariffs,
  parseRoomCategoryBlock,
} from '@/utils/benefits/hotel/excelPaste/parseRoomCategoryBlock';
import { classifyPastedTariffs } from '@/utils/benefits/hotel/excelPaste/multiTariffPaste';
import { computeRoomCategoryPasteOutcome } from '@/utils/benefits/hotel/excelPaste/parseRoomTariffBlock';
import { ROOM_TYPE_KEYS } from '@/utils/benefits/hotel/excelPaste/roomCompositions';
import ExcelPastePanel, { ExcelPasteRecapRow } from './ExcelPastePanel';
import {
  buildLabelAndPeriodsRecap,
  buildTariffNotes,
  FormPeriod,
  toChildBandsCount,
} from './pasteFormHelpers';
import {
  buildRoomAmountsRecap,
  buildRoomOptionsNotes,
  writePastedRoomTariff,
} from './roomPasteHelpers';
import {
  getCategoryTickedOptions,
  Tariff,
  updateCategoryRoomOptions,
  waitForEffects,
} from './updateCategoryRoomOptions';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';

interface RoomCategoryPasteProps {
  categoryIndex: number;
  /** Pasting is only offered for room prices ("Total chambre"). */
  pricingType?: 'byPerson' | 'byRoom';
  /** Adds blank tariff cards at the end of the category. */
  appendBlankTariffs: (count: number) => void;
}

const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

/** A card added but never filled: no label, no date, no price. */
const isBlankTariff = (tariff: Tariff) =>
  !tariff.name?.trim() &&
  (tariff.periods ?? []).every(
    (period) => !period.startDate && !period.endDate,
  ) &&
  ROOM_TYPE_KEYS.every((roomType) =>
    (tariff.pricePartitions?.[roomType] ?? []).every(
      (partition) => !hasValue(partition?.price),
    ),
  );

/**
 * Paste of every tariff of a category at once: the 28×2 blocks of the
 * "Catégorie" tab side by side. Each pasted tariff updates the tariff of the
 * same label, fills a blank tariff or is added; the other tariffs stay as
 * they are. Compositions then follow the prices, as with a single tariff.
 */
const RoomCategoryPaste = ({
  categoryIndex,
  pricingType,
  appendBlankTariffs,
}: RoomCategoryPasteProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } =
    useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const childAges = useWatch({ control, name: 'childAges' });
  const { formatIssue, formatComposition } = useExcelPasteFormatters(childAges);

  const categoryPath = `hotelRoomCategories.${categoryIndex}` as const;

  const parse = useCallback(
    (text: string) => {
      const childBandsCount = toChildBandsCount(
        getValues('acceptsChildPricing') === 'true'
          ? (getValues('childAges')?.length ?? 0)
          : 0,
      );
      const category = getValues(categoryPath);
      const tariffs = (category.tarifs ?? []) as Tariff[];
      const tickedOptions = getCategoryTickedOptions(category);
      const parsed = parseRoomCategoryBlock(text, childBandsCount);
      if (!parsed.value) {
        return {
          childBandsCount,
          tariffs,
          tickedOptions,
          parsed,
          plan: null,
          computed: null,
        };
      }

      const plan = matchPastedRoomTariffs(
        parsed.value,
        tariffs.map((tariff) => ({
          name: tariff.name,
          isBlank: isBlankTariff(tariff),
        })),
      );
      const computed = computeRoomCategoryPasteOutcome(plan.targets, {
        childBandsCount,
        roomOptions: tickedOptions,
        roomTypes: category.roomTypes ?? {},
        tariffs: [
          ...tariffs.map((tariff, index) => ({
            name:
              tariff.name ||
              t('hotels.roomCategory.tarifs.tarif_', { index: index + 1 }),
            periods: tariff.periods ?? [],
            pricePartitions: tariff.pricePartitions,
          })),
          ...Array.from({ length: plan.newTariffCount }, () => ({
            name: '',
            periods: [],
            pricePartitions: {
              individual: [],
              double: [],
              triple: [],
              quadruple: [],
            },
          })),
        ],
      });
      return {
        childBandsCount,
        tariffs,
        tickedOptions,
        parsed,
        plan,
        computed,
      };
    },
    [categoryPath, getValues, t],
  );

  const analyze = useCallback(
    (text: string) => {
      const {
        childBandsCount,
        tariffs,
        tickedOptions,
        parsed,
        plan,
        computed,
      } = parse(text);
      if (!parsed.value || !plan || !computed) {
        return {
          errors: parsed.errors.map(formatIssue),
          warnings: [],
          notes: [],
          recap: [],
        };
      }

      const tariffNotes = buildTariffNotes(
        classifyPastedTariffs(
          plan.targets,
          tariffs.map((tariff) => ({
            name: tariff.name,
            isBlank: isBlankTariff(tariff),
          })),
          (index) =>
            t('hotels.roomCategory.tarifs.tarif_', { index: index + 1 }),
        ),
        t,
      );

      const recap: ExcelPasteRecapRow[] = plan.targets.flatMap(
        ({ tariffIndex, pasted }) => {
          const current = tariffs[tariffIndex];
          return [
            ...buildLabelAndPeriodsRecap(
              {
                name: current?.name,
                periods: current?.periods as FormPeriod[] | undefined,
              },
              pasted,
              t,
            ),
            ...buildRoomAmountsRecap(
              current,
              tickedOptions,
              pasted,
              childBandsCount,
              formatComposition,
            ),
          ].map((row) => ({
            ...row,
            key: `${tariffIndex}-${row.key}`,
            label: `${pasted.name} · ${row.label}`,
          }));
        },
      );

      return {
        errors: [],
        warnings: computed.warnings.map(formatIssue),
        notes: [
          ...tariffNotes,
          ...buildRoomOptionsNotes(
            computed.outcome,
            childBandsCount,
            formatComposition,
            t,
          ),
        ],
        recap,
      };
    },
    [formatComposition, formatIssue, parse, t],
  );

  const apply = useCallback(
    async (text: string) => {
      const { plan, computed } = parse(text);
      if (!plan || !computed) return;
      const nextOptions = computed.outcome.roomOptions;
      const form = { getValues, setValue };

      if (plan.newTariffCount > 0) {
        appendBlankTariffs(plan.newTariffCount);
        await waitForEffects();
      }
      await updateCategoryRoomOptions(form, categoryIndex, nextOptions);
      markPasted(
        plan.targets.flatMap(({ tariffIndex, pasted }) =>
          writePastedRoomTariff(
            form,
            `${categoryPath}.tarifs.${tariffIndex}`,
            pasted,
            nextOptions,
          ),
        ),
      );
    },
    [
      appendBlankTariffs,
      categoryIndex,
      categoryPath,
      getValues,
      markPasted,
      parse,
      setValue,
    ],
  );

  return (
    <ExcelPastePanel
      shape="roomCategory"
      title={t('hotels.pasteFromExcel.categoryButton')}
      analyze={analyze}
      apply={apply}
      disabledReason={
        pricingType === 'byPerson'
          ? t('hotels.pasteFromExcel.byPersonDisabled')
          : undefined
      }
    />
  );
};

export default RoomCategoryPaste;
